import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconArrowLeft, IconLoader2, IconReceipt,
} from "@tabler/icons-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import PricingGrid from "./PricingGrid";

export default function Billing() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasPaidPurchase = txs.some((t) => t.payment_status === "paid");
  // Clientes con compra pagada: nunca ven planes ni precios.
  // Admins (psicóloga) siempre los ven para poder gestionar.
  const plansVisible = !hasPaidPurchase || user?.is_admin;

  const loadAll = useCallback(async () => {
    try {
      const [pkgs, tx] = await Promise.all([
        api.get("/payments/packages"),
        api.get("/payments/my-transactions"),
      ]);
      setPackages(pkgs.data);
      setTxs(tx.data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  return (
    <>
      <div className="subbar">
        <button className="btn-ghost btn text-[12px] px-2 py-1" onClick={() => nav("/app")} data-testid="billing-back">
          <IconArrowLeft size={13} /> {lang === "es" ? "Volver al inicio" : "Back"}
        </button>
        <span className="t">{lang === "es" ? "Facturación · Planes y compras" : "Billing · Plans & purchases"}</span>
      </div>

      <div className="wrap-app max-w-[980px] py-10">
        {/* Si el usuario ya tiene compra pagada, mostramos aviso simple */}
        {hasPaidPurchase && !user?.is_admin && (
          <div className="card mb-6 border-l-[4px] border-l-teal bg-teal-soft" data-testid="already-customer-notice">
            <p className="text-[13.5px] font-medium">
              {lang === "es"
                ? "Tienes una compra activa. Consulta tu historial abajo."
                : "You have an active purchase. See your history below."}
            </p>
          </div>
        )}

        {plansVisible && (<>
          <h3 className="font-display text-[22px] font-medium mb-1">{lang === "es" ? "Planes disponibles" : "Available plans"}</h3>
          <p className="text-tert text-[12.5px] mb-3">
            {lang === "es"
              ? "Precios expresados en USD. Selecciona un plan para continuar al checkout con Tarjeta o PayPal."
              : "Prices in USD. Pick a plan to continue to checkout with Card or PayPal."}
          </p>
          <PricingGrid packages={packages} lang={lang} loading={loading} />
          {error && <p className="text-errtext text-[13px] mb-4">{error}</p>}
        </>)}

        {/* Transaction history */}
        <h3 className="font-display text-[22px] font-medium mb-3 flex items-center gap-2"><IconReceipt size={16} /> {lang === "es" ? "Historial" : "History"}</h3>
        {loading ? (
          <div className="card"><IconLoader2 className="animate-spin" /></div>
        ) : txs.length === 0 ? (
          <p className="text-tert text-[13px]">{lang === "es" ? "Sin transacciones aún." : "No transactions yet."}</p>
        ) : (
          <div className="space-y-2">
            {txs.map((t) => (
              <div key={t.id} className="card flex items-center justify-between gap-3 flex-wrap" data-testid={`tx-${t.session_id}`}>
                <div>
                  <p className="font-medium text-[13.5px]">{t.course_title || t.package_id}</p>
                  <p className="text-tert text-[11.5px] mt-0.5">
                    {new Date(t.created_at).toLocaleString(lang === "es" ? "es-HN" : "en-US")} · {t.provider}
                    {t.user_email && user?.is_admin ? ` · ${t.user_email}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-display text-[15px] font-medium">${(t.amount || 0).toFixed(2)}</span>
                  <span className={`tag ${
                    t.payment_status === "paid" ? "bg-okbg text-oktext" :
                    t.payment_status === "pending" ? "bg-warnbg text-warntext" :
                    "bg-errbg text-errtext"
                  }`}>{t.payment_status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
